/**
 * Identidad del negocio.
 *
 * Viene del servidor y no de una constante del frontend, para que instalar el
 * sistema en otra tienda sea cambiar tres lineas del `.env` y reiniciar, sin
 * recompilar ni ir cazando el nombre del cliente anterior por los archivos.
 *
 * Se consulta antes de iniciar sesion, asi que la ruta es publica. Mientras
 * llega se usa un nombre neutro: nunca se muestra una pantalla en blanco
 * esperando el nombre de la tienda.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';

export interface Branding {
  name: string;
  shortName: string;
  tagline: string;
}

const FALLBACK: Branding = { name: 'Farmacia San Carlos', shortName: 'San Carlos', tagline: '' };

function useBrandingQuery() {
  return useQuery({
    queryKey: ['branding'],
    queryFn: () => api<Branding>('/api/branding'),
    // El nombre del negocio no cambia mientras el sistema esta abierto.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

export function useBranding(): Branding {
  return useBrandingQuery().data ?? FALLBACK;
}

/**
 * Igual que `useBranding`, pero avisa si el nombre todavia no ha llegado.
 *
 * La pantalla de acceso lo necesita: mostrar el nombre de respaldo y cambiarlo
 * medio segundo despues hace que el sistema parezca dudar de como se llama el
 * negocio, y eso pasa justo en la primera impresion. Es preferible reservar el
 * espacio y escribir el nombre una sola vez, ya correcto.
 */
export function useBrandingWhenReady(): { branding: Branding; ready: boolean } {
  const query = useBrandingQuery();
  return {
    branding: query.data ?? FALLBACK,
    // Si la peticion falla tambien se considera "listo": mas vale el nombre
    // generico que una pantalla de acceso en blanco para siempre.
    ready: query.isSuccess || query.isError,
  };
}
